import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Dropdown, Modal, Row, Select, Space, Table, Typography } from "antd";
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { EllipsisOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import olreadiaService from "services/olreadiaService";
import { useAuth } from "contexts/AuthContext";
import { formatEnumLabel } from "../constants";
import ServiceOfferingDetailsDrawer from "../services/ServiceOfferingDetailsDrawer";
import ServiceOfferingDrawer from "../services/ServiceOfferingDrawer";

const toMessage = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

export default function ServicesManagerPanel({
  businessEntityId,
}) {
  const { user } = useAuth();
  const actorId = user?.id || "";
  const [loading, setLoading] = useState(false);
  const [offeringsLoading, setOfferingsLoading] = useState(false);
  const [allOfferings, setAllOfferings] = useState([]);
  const [attached, setAttached] = useState([]);
  const [selectedToAdd, setSelectedToAdd] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadAllOfferings = async () => {
    setOfferingsLoading(true);
    try {
      const res = await olreadiaService.serviceOfferings.list({ page: 0, size: 500 });
      setAllOfferings(Array.isArray(res?.content) ? res.content : []);
    } catch (err) {
      toast.error(toMessage(err, "Failed to load service offerings"));
    } finally {
      setOfferingsLoading(false);
    }
  };

  const loadAttached = async () => {
    if (!businessEntityId) return;
    setLoading(true);
    try {
      const res =
        await olreadiaService.serviceOfferings.businessEntityServices.list(
          businessEntityId,
        );
      setAttached(Array.isArray(res) ? res : []);
    } catch (err) {
      toast.error(toMessage(err, "Failed to load attached services"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllOfferings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessEntityId]);

  useEffect(() => {
    setSelectedToAdd([]);
    loadAttached();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessEntityId]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [attached.length]);

  const attachedIds = useMemo(() => new Set(attached.map((s) => s.id)), [attached]);
  const addOptions = useMemo(
    () =>
      allOfferings
        .filter((o) => !attachedIds.has(o.id))
        .map((o) => ({
          value: o.id,
          label: `${o.name} (${o.code})`,
        })),
    [allOfferings, attachedIds],
  );

  const handleAdd = async () => {
    if (!selectedToAdd.length || !businessEntityId) return;
    setLoading(true);
    try {
      for (const serviceId of selectedToAdd) {
        await olreadiaService.serviceOfferings.businessEntityServices.add(
          businessEntityId,
          serviceId,
          actorId,
        );
      }
      toast.success("Services attached");
      setSelectedToAdd([]);
      await loadAttached();
    } catch (err) {
      toast.error(toMessage(err, "Failed to attach service(s)"));
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = (record) => {
    Modal.confirm({
      title: "Remove service?",
      content: "This will detach the service from this target.",
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        setLoading(true);
        try {
          await olreadiaService.serviceOfferings.businessEntityServices.remove(
            businessEntityId,
            record.id,
            actorId,
          );
          toast.success("Service removed");
          await loadAttached();
        } catch (err) {
          toast.error(toMessage(err, "Failed to remove service"));
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const openDetails = (record) => {
    setSelected(record);
    setDetailsOpen(true);
  };

  const handleEdit = (record) => {
    setEditing(record);
    setEditOpen(true);
  };

  const handleEditSubmit = async (payload) => {
    if (!editing?.id) return;
    setLoading(true);
    try {
      await olreadiaService.serviceOfferings.update(editing.id, payload, actorId);
      toast.success("Service offering updated");
      setEditOpen(false);
      await Promise.all([loadAllOfferings(), loadAttached()]);
    } catch (err) {
      toast.error(toMessage(err, "Failed to update service offering"));
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: "Code", dataIndex: "code", key: "code", ellipsis: true },
    { title: "Name", dataIndex: "name", key: "name", ellipsis: true },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (val) => (val ? formatEnumLabel(val) : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="be-actions" onClick={(e) => e.stopPropagation()}>
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "remove",
                  label: "Remove",
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    handleRemove(record);
                  },
                },
              ],
            }}
          >
            <Button size="small" type="text" aria-label="Actions">
              <EllipsisOutlined style={{ fontSize: 18 }} />
            </Button>
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <div style={{ marginTop: 8 }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <div style={{ flex: "1 1 340px", maxWidth: 360 }}>
          <Select
            mode="multiple"
            showSearch
            optionFilterProp="label"
            style={{ width: "100%" }}
            placeholder="Search & select service offerings to attach"
            loading={offeringsLoading}
            value={selectedToAdd}
            onChange={setSelectedToAdd}
            options={addOptions}
            maxTagCount={2}
          />
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          loading={loading}
          onClick={handleAdd}
          disabled={!selectedToAdd.length}
        >
          Add
        </Button>
      </div>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={attached}
        pagination={{
          ...pagination,
          total: attached.length,
          showSizeChanger: true,
          onChange: (current, pageSize) => setPagination({ current, pageSize }),
        }}
        size="small"
        className="custom-table"
        tableLayout="auto"
        scroll={{ x: "max-content" }}
        style={{ borderRadius: 10, overflow: "hidden" }}

        onRow={(record) => ({
          onClick: (e) => {
            if (e?.target?.closest?.(".be-actions")) return;
            openDetails(record);
          },
        })}
      />

      <ServiceOfferingDetailsDrawer
        open={detailsOpen}
        offering={selected}
        onClose={() => setDetailsOpen(false)}
        onAttach={null}
        onEdit={(record) => {
          setDetailsOpen(false);
          handleEdit(record);
        }}
      />

      <ServiceOfferingDrawer
        open={editOpen}
        initialValues={editing}
        loading={loading}
        onClose={() => {
          setEditOpen(false);
          setEditing(null);
        }}
        onSubmit={handleEditSubmit}
      />
    </div>
  );
}
